import React, { useEffect, useState } from 'react';

/**
 * Cycle through a list of words with a typewriter / wipe transition. Renders an
 * inline-block so it can sit inside a heading.
 *
 * Props:
 *  - words: string[]
 *  - interval: ms between word changes (default 2800)
 *  - className: additional classes
 */
export const RotatingWords = ({ words = [], interval = 2800, className = '' }) => {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('in'); // 'in' | 'out'

  useEffect(() => {
    if (!words.length) return undefined;
    const out = setTimeout(() => setPhase('out'), interval - 600);
    const change = setTimeout(() => {
      setIdx((i) => (i + 1) % words.length);
      setPhase('in');
    }, interval);
    return () => {
      clearTimeout(out);
      clearTimeout(change);
    };
  }, [idx, interval, words.length]);

  if (!words.length) return null;
  const word = words[idx];

  return (
    <span
      className={`relative inline-block align-baseline overflow-hidden ${className}`}
      data-testid="rotating-words"
    >
      <span
        key={`${idx}-${phase}`}
        className={`inline-block ${
          phase === 'in'
            ? 'animate-rotating-word-in'
            : 'animate-rotating-word-out'
        }`}
      >
        {word}
      </span>
      {/* Trailing cursor */}
      <span className="ml-1 inline-block w-[2px] h-[0.85em] align-baseline bg-current opacity-70 animate-cursor-blink" />
    </span>
  );
};

export default RotatingWords;
